import React from 'react';
import { View, Text, StyleSheet, ViewStyle, TextStyle, Platform } from 'react-native';

export interface ChatMessageBubbleProps {
  /** 消息内容 */
  message: string;
  /** 消息时间 */
  time: string;
  /** 是否为自己发送的消息 */
  isOwn: boolean;
  /** 发送者名称 (仅对方消息显示) */
  senderName?: string;
  /** 主题颜色 */
  themeColor?: string;
  /** 容器样式 */
  containerStyle?: ViewStyle;
  /** 气泡样式 */
  bubbleStyle?: ViewStyle;
  /** 消息文本样式 */
  messageStyle?: TextStyle;
  /** 时间文本样式 */
  timeStyle?: TextStyle;
  /** 最大宽度百分比 (默认 80%) */
  maxWidth?: string;
}

/**
 * 聊天消息气泡组件
 * 
 * 显示单条聊天消息，支持自己/对方两种样式
 * 
 * @example
 * ```tsx
 * <ChatMessageBubble
 *   message="你好！"
 *   time="12:30"
 *   isOwn={false}
 *   senderName="张三"
 *   themeColor="#4A90E2"
 * />
 * ```
 */
export const ChatMessageBubble: React.FC<ChatMessageBubbleProps> = ({
  message,
  time,
  isOwn,
  senderName,
  themeColor = '#4A90E2',
  containerStyle,
  bubbleStyle,
  messageStyle,
  timeStyle,
  maxWidth = '80%',
}) => {
  return (
    <View 
      style={[
        styles.container,
        isOwn ? styles.ownContainer : styles.otherContainer,
        containerStyle,
      ]}
    >
      {/* 发送者名称 (仅对方消息) */}
      {!isOwn && senderName && (
        <View style={styles.senderHeader}>
          <Text style={styles.senderName}>{senderName}</Text>
        </View>
      )}
      
      {/* 消息气泡 */}
      <View 
        style={[
          styles.bubble,
          { maxWidth },
          isOwn 
            ? [styles.ownBubble, { backgroundColor: themeColor }]
            : styles.otherBubble,
          bubbleStyle,
        ]}
      >
        <Text 
          style={[
            styles.message,
            isOwn ? styles.ownMessage : styles.otherMessage,
            messageStyle,
          ]}
        >
          {message}
        </Text>
        
        <Text 
          style={[
            styles.time,
            isOwn ? styles.ownTime : styles.otherTime,
            timeStyle,
          ]}
        >
          {time}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 12,
  },
  ownContainer: {
    alignItems: 'flex-end',
  },
  otherContainer: {
    alignItems: 'flex-start',
  },
  senderHeader: {
    marginBottom: 4,
    marginHorizontal: 12,
  },
  senderName: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  bubble: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 18,
  },
  ownBubble: {
    borderBottomRightRadius: 4,
  },
  otherBubble: {
    backgroundColor: '#fff',
    borderBottomLeftRadius: 4,
    ...Platform.select({
      web: {
        boxShadow: '0px 1px 2px rgba(0,0,0,0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 1,
        },
        shadowOpacity: 0.1,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  message: {
    fontSize: 16,
    lineHeight: 20,
    marginBottom: 4,
  },
  ownMessage: {
    color: '#fff',
  },
  otherMessage: {
    color: '#333',
  },
  time: {
    fontSize: 11,
    alignSelf: 'flex-end',
  },
  ownTime: {
    color: 'rgba(255, 255, 255, 0.8)',
  },
  otherTime: {
    color: '#999',
  },
});
